/**
 * newFeature — T327. His words: *"in the procurement, we always have the "new" tag, remove it,
 * when a new feature is added you can bring a chip "New" FEATURE but, it should be removed when
 * the user click on it"*.
 *
 * ⚠ THE OLD ONE WAS A HARDCODED `<Chip label="New">` ON PROCUREMENT WITH NO EXPIRY AND NO MEMORY.
 * It had been shouting "new" at him for months, which is precisely how a badge stops meaning
 * anything: a permanent NEW is decoration, and the next genuinely new thing inherits an audience
 * that has learned to ignore it.
 *
 * So the badge is now a mechanism with three properties:
 *   · ONE LIST says what is new (below). Adding a feature is adding a line here, and nothing in
 *     any page decides this for itself.
 *   · IT IS DISMISSED BY USE — the chip disappears the moment he opens the thing. No settings
 *     switch, no "mark all read".
 *   · IT EXPIRES ON ITS OWN. A feature nobody ever clicked still stops being new after
 *     `MAX_AGE_DAYS`, so a forgotten line here cannot become another permanent badge.
 *
 * Seen-state is per PERSON and per DEVICE, in localStorage: a nudge is not data worth a database
 * row, and a colleague on another machine has not seen it yet either.
 */

/** ISO date the feature shipped — used for the expiry, so nothing here lives for ever. */
export interface NewFeature {
  /** The route this badge belongs to, exactly as the sidebar links to it. */
  path: string;
  /** When it shipped. */
  since: string;
}

/** A badge stops being new 30 days after it shipped, clicked or not. */
export const MAX_AGE_DAYS = 30;

/**
 * THE LIST. Add a line when something ships; delete it whenever you like — an entry older than
 * MAX_AGE_DAYS is already invisible.
 */
export const NEW_FEATURES: NewFeature[] = [
  /* T292/T295 — the Standards page, which is genuinely new to him. Procurement is NOT here:
     it is the one he asked us to stop badging. */
  { path: '/standards', since: '2026-09-18' },
];

const SEEN_KEY = 'forge_seen_features';

function read(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') || {}; } catch { return {}; }
}

function write(map: Record<string, string>): void {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(map)); } catch { /* private mode */ }
}

function withinMaxAge(since: string): boolean {
  const shipped = Date.parse(since);
  if (!Number.isFinite(shipped)) return false;
  return Date.now() - shipped < MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
}

/** Should this path wear the chip right now? */
export function isFeatureNew(path: string, userKey?: string | number | null): boolean {
  const feature = NEW_FEATURES.find(f => f.path === path);
  if (!feature || !withinMaxAge(feature.since)) return false;
  return !read()[`${userKey ?? 'anon'}|${path}`];
}

/** He opened it — that is the dismissal, and it is the only one. */
export function markFeatureSeen(path: string, userKey?: string | number | null): void {
  if (!NEW_FEATURES.some(f => f.path === path)) return;
  const map = read();
  map[`${userKey ?? 'anon'}|${path}`] = new Date().toISOString();
  write(map);
}
